import { useState, 
         useEffect, 
         useRef, 
         useCallback 
       }                                  from "react";
import { useGlobalConfig }                from "context/GlobalContext";
import { Box, 
         Typography, 
         Card, 
         CardContent, 
         Button, 
         Alert 
       }                                  from '@mui/material';
import CircularProgressWithLabel          from 'components/loading/CircularProgressWithLabel';
import { BglLoading }                     from "components/loading/BglLoading";
import { ROUTE_TREE }                     from "routes/AppRoutes";
import { useParams }                      from "react-router-dom";
import { CMSPilotApis }                   from "apis/cmsPilotAPI";
import { GetCMSPilotCreationStatus }         from "types/apis/cmsPilotInterfaces";

export const CMSPilotCreationStatusPage = () => {

  const { rc } = useGlobalConfig();
  const { id } = useParams();
  
  const [status, setStatus]   = useState<GetCMSPilotCreationStatus.retval>();
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | undefined>();
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  const handleBackToHome = () => {
    rc.uiRouter.navigate(ROUTE_TREE.CMS.PROJECT_LIST);
  };

  const navToContent = async () => {
    rc.uiRouter.navigate(ROUTE_TREE.CMS.CONTENT);
  };

  const fetchPilotCreationStatus = useCallback(async () =>  {

    const data = JSON.parse(id!) as GetCMSPilotCreationStatus.retval;
    
    const response = await CMSPilotApis.getPilotCreationStatus(rc, data.jobId);

    if (response.data) {
      setStatus(response.data);
      setError(undefined);    
      if (response.data.jobStatus === 'COMPLETED') {
        await rc.session.selectCMSPilotByPilotId(response.data.pilotId)
      }
    } else {
      setError('Failed to fetch pilot creation status');
    }
    setLoading(false);
  }, [id, rc]); 

  useEffect(() => {
    // Initial fetch
    fetchPilotCreationStatus();

    if (status?.jobStatus === 'COMPLETED' || status?.jobStatus === 'FAILED') {
      return;
    }

    // Set up polling interval
    pollingIntervalRef.current = setInterval(() => {
      fetchPilotCreationStatus();
    }, 2000); // Poll every 2 seconds

    // Cleanup interval on unmount
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [fetchPilotCreationStatus]);

  // Effect to stop polling when job is completed or failed
  useEffect(() => {
    if (status?.jobStatus === 'COMPLETED' || status?.jobStatus === 'FAILED') {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    }
  }, [status?.jobStatus]);

  return (
    <Box sx={{ padding: 3 }}>
      
      <Card sx={{ maxWidth: 600, margin: '0 auto' }}>
        <CardContent sx={{ padding: 4 }}>
          <Box sx={{ textAlign: 'center', marginBottom: 3 }}>
            {loading ? (
              <BglLoading className="" />
            ) : error ? (
              <Alert severity="error" sx={{ marginBottom: 2 }}>
                {error}
              </Alert>
            ) : status ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <CircularProgressWithLabel
                  value={status.progress}
                  size={120}
                  thickness={4}
                  sx={{
                    color: status.jobStatus === 'FAILED' ? 'error.main' : 
                           status.jobStatus === 'COMPLETED' ? 'success.main' : 'primary.main'
                  }}
                />
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  {status.jobStatus === 'COMPLETED' && 'Pilot Creation Completed!'}
                  {status.jobStatus === 'FAILED' && 'Pilot Creation Failed'}
                  {status.jobStatus !== 'COMPLETED' && status.jobStatus !== 'FAILED' && 'Creating Pilot...'}
                </Typography>
              </Box>
            ) : (
              <BglLoading className="" />
            )}
          </Box>

          {!error && (
            <Alert 
              severity={
                status?.jobStatus === 'COMPLETED' ? 'success' :
                status?.jobStatus === 'FAILED' ? 'error' : 'info'
              } 
              sx={{ marginBottom: 3 }}
            >
              {status?.jobStatus === 'COMPLETED' && 'Your pilot has been successfully created and is ready to use.'}
              {status?.jobStatus === 'FAILED' && 'There was an error creating your pilot. Please try again or contact support.'}
              {status?.jobStatus !== 'COMPLETED' && status?.jobStatus !== 'FAILED' && 'Your pilot is being created in the background. This process may take a few minutes.'}
            </Alert>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
            {status?.jobStatus === 'COMPLETED' ? (
              <>
                <Button
                  variant="contained"
                  onClick={navToContent}
                  size="large"
                  sx={{ backgroundColor: '#003366', '&:hover': { backgroundColor: '#002244' } }}
                >
                  Manage Content
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleBackToHome}
                  size="large"
                >
                  Back to Dashboard
                </Button>
              </>
            ) : (
              <Button
                variant="contained"
                onClick={handleBackToHome}
                size="large"
              >
                Back to Dashboard
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
